import React, { useState } from 'react';
import {
    Box,
    Button,
    Typography,
} from '@mui/material';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { LOCAL_STORAGE_KEYS } from '../../../utils/constants';
import { getCountryCode } from '../../../utils/utils';
import { CreatePaymentIntentService, UpdateBillingOrgService } from '../../../services/billing.service';
import PromoCodeInput from './PromoCodeInput.comp';

const StripePaymentForm = ({
    invoice,
    promoState,
    setPromoState,
    onClose,
    setLoading,
    fetchBillingInfo,
    dqInsiderInfo,
    formatAmount,
}) => {
    const stripe = useStripe();
    const elements = useElements();
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState(null);

    // Derive the amount that will actually be charged
    const payAmount = promoState?.finalAmount ?? invoice?.total_amount ?? 0;

    // Get billing details from DQ Insider info
    const getBillingName = () => {
        if (dqInsiderInfo?.profile?.profile_type === 'BUSINESS') {
            return dqInsiderInfo.profile.business_name || 'Business';
        }
        if (dqInsiderInfo?.profile?.first_name && dqInsiderInfo?.profile?.last_name) {
            return `${dqInsiderInfo.profile.first_name} ${dqInsiderInfo.profile.last_name}`;
        }
        return 'Customer';
    };

    const getBillingEmail = () => {
        return dqInsiderInfo?.user?.email || localStorage.getItem(LOCAL_STORAGE_KEYS.user_email) || '';
    };

    const getBillingAddress = () => {
        const profile = dqInsiderInfo?.profile;
        if (!profile) return {};
        return {
            line1: profile.address || '',
            city: profile.city || '',
            postal_code: profile.postal_code || '',
            country: getCountryCode(profile.country),
        };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return;

        setProcessing(true);
        setError(null);

        try {
            // Step 1 — Create the PaymentIntent server-side (with promo if any).
            const intentData = {
                billing_month: invoice.billing_month,
                amount: payAmount,
                currency: 'USD',
                ...(promoState?.promoCode ? { promo_code: promoState.promoCode } : {}),
            };
            const resp = await CreatePaymentIntentService(intentData);
            const { client_secret: secret, zero_amount } = resp.data;

            // ── Zero-amount path ──────────────────────────────────────────
            if (zero_amount) {
                const updateResp = await UpdateBillingOrgService({
                    billing_month: invoice.billing_month,
                });
                console.log(updateResp);
                onClose();
                setLoading(true);
                await fetchBillingInfo();
                return;
            }

            // ── Normal Stripe path (amount > 0) ──────────────────────────
            const card = elements.getElement(CardElement);
            const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
                type: 'card',
                card,
                billing_details: {
                    name: getBillingName(),
                    email: getBillingEmail(),
                    address: getBillingAddress(),
                },
            });

            if (pmError) {
                setError(pmError.message);
                return;
            }

            const result = await stripe.confirmCardPayment(secret, {
                payment_method: paymentMethod.id,
            });

            if (result.error) {
                console.error('Payment Error:', result.error.message);
                setError(result.error.message);
            } else {
                if (result.paymentIntent.status === 'succeeded') {
                    const data_to_update = {
                        billing_month: invoice.billing_month,
                        stripe_resp: result.paymentIntent,
                    };
                    const updateResp = await UpdateBillingOrgService(data_to_update);
                    console.log(updateResp);

                    onClose();
                    setLoading(true);
                    await fetchBillingInfo();
                } else {
                    onClose();
                }
            }
        } catch (err) {
            console.error('Payment error:', err);
            setError('An unexpected error occurred. Please try again.');
        } finally {
            setProcessing(false);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            {/* Billing Information Display */}
            <Box sx={{ mb: 3, p: 2, backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5, color: '#000' }}>
                    Billing Information
                </Typography>

                <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="textSecondary">
                        Name
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {getBillingName()}
                    </Typography>
                </Box>

                <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="textSecondary">
                        Email
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {getBillingEmail()}
                    </Typography>
                </Box>

                {dqInsiderInfo?.profile?.address && (
                    <Box sx={{ mb: 0 }}>
                        <Typography variant="caption" color="textSecondary">
                            Address
                        </Typography>
                        <Typography variant="body2">
                            {dqInsiderInfo.profile.address}
                        </Typography>
                        <Typography variant="body2">
                            {[dqInsiderInfo.profile.city, dqInsiderInfo.profile.postal_code].filter(Boolean).join(', ')}
                        </Typography>
                        <Typography variant="body2">
                            {dqInsiderInfo.profile.country}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Promo Code */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, color: '#000' }}>
                    Promo Code <Typography component="span" variant="caption" color="textSecondary">(optional)</Typography>
                </Typography>
                <PromoCodeInput
                    billingMonth={invoice?.billing_month}
                    invoiceAmount={invoice?.total_amount ?? 0}
                    onChange={setPromoState}
                />
            </Box>

            {/* Amount to Pay */}
            <Box sx={{ mb: 3, p: 2, backgroundColor: '#fff3e0', borderRadius: '8px', border: '1px solid #ffb74d' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        Amount to Pay:
                    </Typography>
                    <Box sx={{ textAlign: 'right' }}>
                        {promoState?.promoCode && (
                            <Typography variant="body2" sx={{ color: '#9e9e9e', textDecoration: 'line-through', lineHeight: 1 }}>
                                ${formatAmount(invoice?.total_amount)} USD
                            </Typography>
                        )}
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#ff6468' }}>
                            ${formatAmount(payAmount)} USD
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Card Details — hidden when promo covers the full amount */}
            {payAmount > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#000' }}>
                        Card Details
                    </Typography>
                    <Box sx={{
                        p: 2,
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        backgroundColor: '#ffffff',
                        '&:focus-within': {
                            borderColor: '#ff6468',
                            boxShadow: '0 0 0 2px rgba(255, 100, 104, 0.1)'
                        }
                    }}>
                        <CardElement
                            options={{
                                style: {
                                    base: {
                                        fontSize: '16px',
                                        color: '#424770',
                                        '::placeholder': {
                                            color: '#aab7c4',
                                        },
                                    },
                                    invalid: {
                                        color: '#9e2146',
                                    },
                                }
                            }}
                        />
                    </Box>
                </Box>
            )}

            {error && (
                <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#ffebee', borderRadius: '8px', border: '1px solid #ef5350' }}>
                    <Typography color="error" sx={{ fontSize: '14px' }}>
                        {error}
                    </Typography>
                </Box>
            )}

            <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{
                    backgroundColor: '#4caf50',
                    color: '#ffffff',
                    fontWeight: 'bold',
                    py: 1.5,
                    fontSize: '16px',
                    '&:hover': {
                        backgroundColor: '#43a047'
                    },
                    '&:disabled': {
                        backgroundColor: '#e0e0e0',
                        color: '#9e9e9e'
                    }
                }}
                disabled={!stripe || processing}
            >
                {processing
                    ? 'Processing Payment...'
                    : payAmount > 0
                        ? `Pay $${formatAmount(payAmount)} USD`
                        : 'Confirm — $0.00 Due'
                }
            </Button>
        </form>
    );
};

export default StripePaymentForm;
